import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Preloader.css';

const Preloader = ({ onComplete }) => {
  const [interactions, setInteractions] = useState(0);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [activeKey, setActiveKey] = useState(null);

  // --- NEW: Audio References ---
  const vaultAudioRef = useRef(null);
  const keypressAudioRef = useRef(null); // Optional: Subtle beep for typing

  useEffect(() => {
    // Initialize audio inside useEffect to prevent React Snap (SSR) build errors
    vaultAudioRef.current = new Audio('/sounds/vault-open.mp3');
    vaultAudioRef.current.volume = 0.8; // Set volume to 80%

    // Optional: A tiny click/beep sound for the 3 pins
    keypressAudioRef.current = new Audio('/sounds/keystroke.mp3');
    keypressAudioRef.current.volume = 0.2; 
  }, []);

  const handleTrigger = useCallback(() => {
    if (isUnlocked) return;
    
    // Play keystroke sound if available
    if (keypressAudioRef.current) {
      keypressAudioRef.current.currentTime = 0; // Reset to start so rapid clicks don't overlap weirdly
      keypressAudioRef.current.play().catch(() => {}); // Catch ignores browser autoplay blocks
    }

    setInteractions((prev) => {
      const nextCount = prev + 1;
      if (nextCount >= 3) {
        setIsUnlocked(true); 

        // --- NEW: Play Vault Opening Sound ---
        if (vaultAudioRef.current) {
          vaultAudioRef.current.play().catch(e => console.log("Audio play failed:", e));
        }

        setTimeout(() => {
          setIsFadingOut(true);
        }, 1500);

        setTimeout(() => {
          onComplete();
        }, 2500);
        return 3;
      }
      return nextCount;
    });
  }, [isUnlocked, onComplete]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key) || e.key === '*' || e.key === '#') {
        setActiveKey(e.key);
        handleTrigger();
      }
    };

    const handleKeyUp = () => {
      setActiveKey(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleTrigger]);

  return (
    <div className={`preloader-overlay ${isFadingOut ? 'fade-out' : ''}`}>
      
      <div className="atmospheric-dust"></div>
      <div className="volumetric-rays"></div>

      <div className="cinematic-container">
        
        {/* LEFT: NUMPAD */}
        <div className="numpad-wrapper">
          <div className="outer-bevel" onClick={(e) => e.stopPropagation()}>
            
            <div className="frame-bolt tl"><div className="bolt-inner"></div></div>
            <div className="frame-bolt tr"><div className="bolt-inner"></div></div>
            <div className="frame-bolt bl"><div className="bolt-inner"></div></div>
            <div className="frame-bolt br"><div className="bolt-inner"></div></div>

            <div className="numpad-instruction">
              <div className="auth-req">AUTHENTICATION REQUIRED</div>
              <div className="auth-sub">Use a 3-digit access key</div>
              <div className="auth-hint">Hint: There is no wrong key.</div>
            </div>

            <div className="numpad-status-bar">
               <div className="status-leds">
                <span className={`led ${interactions >= 1 ? 'active' : ''}`}></span>
                <span className={`led ${interactions >= 2 ? 'active' : ''}`}></span>
                <span className={`led ${interactions >= 3 ? 'active' : ''}`}></span>
              </div>
            </div>

            <div className="numpad-screen-area">
              <div className="numpad-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((key) => (
                  <button 
                    key={key} 
                    className={`num-key ${activeKey === key.toString() ? 'pressed' : ''}`}
                    onClick={handleTrigger}
                  >
                    <span className="key-text">{key}</span>
                    <div className="key-glow"></div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: VAULT ASSEMBLY */}
        <div className="vault-door-visual">
          <div className="vault-outer-ring">
            
            {[...Array(12)].map((_, i) => (
              <div key={i} className="ring-bolt-housing" style={{ transform: `rotate(${i * 30}deg) translateY(-220px)` }}>
                <div className="bolt-head"><div className="bolt-slot"></div></div>
              </div>
            ))}

            <div className="wheel-recess">
                <div className={`vault-inner-wheel ${isUnlocked ? 'spinning' : ''}`}>
                <div className="wheel-hub">
                    <div className="hub-cap"></div>
                </div>
                <div className="wheel-spoke h1"><div className="spoke-ridge"></div></div>
                <div className="wheel-spoke h2"><div className="spoke-ridge"></div></div>
                <div className="wheel-spoke h3"><div className="spoke-ridge"></div></div>
                </div>
            </div>

            <div className="vault-hinge-block">
              <div className="hinge-knuckle top"></div>
              <div className="hinge-bar"></div>
              <div className="hinge-knuckle bottom"></div>
            </div>

          </div>
        </div>

      </div>
      
      <div className="vignette"></div>
    </div>
  );
};

export default Preloader;